import type { Action as ProcessAction } from "../types/action";
import type { ProcessSetupSnapshot } from "../types/state";

// Action type -> binding fields -> cardinality. A binding field names a config key
// that points at one or more artifacts the action acts on. This is the
// action->artifact dependency that skip prunes. Mirror of the Python
// ACTION_TYPE_SCHEMAS in services/py/api/services/process_resolver.py — keep the action
// types and cardinalities in sync (the accepts-type list is validated server-side
// only, so it is intentionally not duplicated here). The legacy
// "instagram_carousel" accept type on publish_instagram_post is deliberately not
// mirrored: it is not a real artifact type.
export type BindingCardinality = "one" | "many";

export const ACTION_ARTIFACT_BINDINGS: Record<
  string,
  { field: string; cardinality: BindingCardinality }[]
> = {
  publish_instagram_post: [{ field: "artifact_ref", cardinality: "one" }],
  publish_instagram_comment_reply: [
    { field: "artifact_ref", cardinality: "one" },
  ],
  send_email_to_contacts: [{ field: "artifact_ref", cardinality: "one" }],
  monitor_instagram_comments_and_reply: [
    { field: "dm_artifact_refs", cardinality: "many" },
  ],
  request_owner_action: [{ field: "artifact_ref", cardinality: "one" }],
};

export type ProcessPlan = {
  // Artifact ids that survive the skip (not in skipped_artifact_ids).
  activeArtifactIds: Set<string>;
  // Actions that still run after the binding + trigger prune.
  survivingActions: ProcessAction[];
  // Action ids dropped because their sole artifact was skipped, or because they
  // are triggered by a dropped action.
  droppedActionIds: Set<string>;
};

// An action binding value may name an artifact by its id OR its ref (mirrors the
// backend `_artifact_matches_ref` and the id-or-ref matcher used elsewhere in the
// setup UI). An unresolved ${...} placeholder never names a concrete artifact.
function refMatchesArtifact(
  refValue: unknown,
  // `ref` is nullable on Artifact — the wire shape carries an explicit null when the
  // artifact has no ref, so accepting only `string | undefined` here rejects real callers.
  artifact: { id: string; ref?: string | null },
): boolean {
  if (typeof refValue !== "string" || refValue.startsWith("${")) return false;
  return refValue === artifact.id || refValue === artifact.ref;
}

// `request_owner_action` binds to an artifact_ref like the publishers do, but it
// is an ask ABOUT the artifact, not the thing that carries it out — its trigger is
// when we nudge the owner, not when the post goes up. It is surfaced separately
// (getOwnerActions), so matching it here would only let an ask's timing
// masquerade as a publish schedule.
const NOT_A_CARRIER = new Set<string>(["request_owner_action"]);

// The action that carries out an artifact — its schedule, and where its send
// status lands. Reads the same binding table the skip prune walks, so "which
// action publishes this post" has one answer in the product rather than a private
// per-surface map. Placeholder (${...}) refs resolve to null: an unresolved
// binding names no concrete artifact, and a row must not claim a schedule it
// cannot show.
export function actionForArtifact(
  actions: readonly ProcessAction[],
  // `ref` is nullable to match Artifact on the wire (types/artifact.ts) — a
  // narrower `string | undefined` would reject the real callers.
  artifact: { id: string; ref?: string | null },
): ProcessAction | null {
  for (const action of actions) {
    if (NOT_A_CARRIER.has(action.type)) continue;
    const bindings = ACTION_ARTIFACT_BINDINGS[action.type];
    if (!bindings) continue;
    const config = (action.config ?? {}) as Record<string, unknown>;
    for (const { field, cardinality } of bindings) {
      if (cardinality !== "one") continue;
      if (refMatchesArtifact(config[field], artifact)) return action;
    }
  }
  return null;
}

/**
 * True when SOME action in the plan names this artifact.
 *
 * A different question from `actionForArtifact`, which answers "which action CARRIES this out"
 * and therefore reads `one` bindings alone and skips `request_owner_action`. Both of those
 * exclusions are wrong here: the monitor's `dm_artifact_refs` is a `many` binding, and an ask's
 * `artifact_ref` still proves the PLANNER wrote the artifact down.
 *
 * What it separates is planned work from run-time output. The comment monitor mints one
 * `instagram_comment_reply` per incoming comment and attaches it to the same Process, and no
 * action names one of those.
 */
export function isPlannedArtifact(
  actions: readonly ProcessAction[],
  artifact: { id: string; ref?: string | null },
): boolean {
  for (const action of actions) {
    const bindings = ACTION_ARTIFACT_BINDINGS[action.type];
    if (!bindings) continue;
    const config = (action.config ?? {}) as Record<string, unknown>;
    for (const { field } of bindings) {
      const value = config[field];
      if (Array.isArray(value)) {
        if (value.some((entry) => refMatchesArtifact(entry, artifact)))
          return true;
      } else if (refMatchesArtifact(value, artifact)) {
        return true;
      }
    }
  }
  return false;
}

// Prune the action->artifact binding graph for the user's skipped artifacts. Two
// passes: (1) binding prune — a `one` binding to a skipped artifact drops the
// action, a `many` binding drops the skipped entries; (2) fixed-point trigger
// cascade — drop any action whose trigger.source_ref names an already-dropped
// action. Placeholder (${...}) edges are intentionally NOT pruned here.
export function deriveProcessPlan(snapshot: ProcessSetupSnapshot): ProcessPlan {
  const skipped = new Set(snapshot.skipped_artifact_ids ?? []);
  const children = snapshot.children ?? [];
  const actions = snapshot.actions ?? [];

  const skippedArtifacts = children.filter((c) => skipped.has(c.id));
  const activeArtifactIds = new Set(
    children.filter((c) => !skipped.has(c.id)).map((c) => c.id),
  );

  const droppedActionIds = new Set<string>();

  // Pass 1: binding prune.
  for (const action of actions) {
    const bindings = ACTION_ARTIFACT_BINDINGS[action.type];
    if (!bindings) continue;
    const config = (action.config ?? {}) as Record<string, unknown>;
    for (const { field, cardinality } of bindings) {
      const value = config[field];
      if (cardinality === "one") {
        if (skippedArtifacts.some((sa) => refMatchesArtifact(value, sa))) {
          droppedActionIds.add(action.id);
        }
      }
      // `many` bindings never drop the action (the monitor survives with fewer
      // DMs); their effective pruning is applied server-side at activation.
    }
  }

  // Pass 2: trigger.source_ref cascade (fixed point).
  const actionByRef = new Map(actions.map((a) => [a.ref, a]));
  let changed = true;
  while (changed) {
    changed = false;
    for (const action of actions) {
      if (droppedActionIds.has(action.id)) continue;
      const sourceRef = (action.trigger as { source_ref?: string } | null)
        ?.source_ref;
      if (!sourceRef || sourceRef.startsWith("system.")) continue;
      const source = actionByRef.get(sourceRef);
      if (source && droppedActionIds.has(source.id)) {
        droppedActionIds.add(action.id);
        changed = true;
      }
    }
  }

  const survivingActions = actions.filter((a) => !droppedActionIds.has(a.id));
  return { activeArtifactIds, survivingActions, droppedActionIds };
}
